import { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import { PencilSquareIcon } from '@heroicons/react/24/outline';

import Button from '../../components/ui/Button';
import PageShell from '../../components/ui/PageShell';
import { useAuth } from '../../context/AuthContext';
import { clientMailService } from '../../services';
import ComposePanel from './ComposePanel';

/** Every Client mail page tells the others when something changed (an approval, a submit). */
export const CLIENT_MAIL_CHANGED = 'client-mail:changed';
export const announceChange = () => window.dispatchEvent(new Event(CLIENT_MAIL_CHANGED));

/** Reload `fn` whenever any Client mail page changes something. */
export function useClientMailChanges(fn) {
  useEffect(() => {
    window.addEventListener(CLIENT_MAIL_CHANGED, fn);
    return () => window.removeEventListener(CLIENT_MAIL_CHANGED, fn);
  }, [fn]);
}

const TABS = [
  { to: '/client-mail', label: 'Overview', end: true },
  { to: '/client-mail/approvals', label: 'Approval queue', badge: 'pendingApproval' },
  { to: '/client-mail/drafts', label: 'My drafts' },
  { to: '/client-mail/mailbox', label: 'Mailbox' },
  { to: '/client-mail/events', label: 'Email events' },
  { to: '/client-mail/settings', label: 'Settings' },
];

/**
 * The frame of every Client mail page: the title, the tabs (with how many
 * emails wait for approval), and Compose — a Gmail-style panel that submits
 * for approval. For admins and the DPM coordinator department.
 */
export default function ClientMailShell({ title, subtitle, actions, children, composeClientId = '' }) {
  const { user } = useAuth();
  const [pending, setPending] = useState(null);
  const [compose, setCompose] = useState(null);

  const loadCount = useCallback(() => {
    clientMailService.summary()
      .then((r) => setPending(r.data?.pendingApproval ?? null))
      .catch(() => {});
  }, []);
  useEffect(() => { loadCount(); }, [loadCount]);
  useClientMailChanges(loadCount);

  /* Another page (a draft in the queue) can open the composer: `openCompose({ draftId })`. */
  useEffect(() => {
    /* Already writing one: a plain "compose" (the floating button) must not wipe it. */
    const open = (e) => setCompose((cur) => (cur && !e.detail?.draftId ? cur : { key: Date.now(), ...(e.detail || {}) }));
    window.addEventListener('client-mail:compose', open);
    return () => window.removeEventListener('client-mail:compose', open);
  }, []);

  return (
    <PageShell
      title={title}
      subtitle={subtitle}
      actions={(
        <div className="flex flex-wrap items-center gap-2">
          {actions}
          {user && (
            <Button type="button" size="sm" onClick={() => setCompose({ key: Date.now(), clientId: composeClientId })}>
              <PencilSquareIcon className="mr-1 h-4 w-4" />
              Compose
            </Button>
          )}
        </div>
      )}
    >
      <nav aria-label="Client mail" className="-mt-1 flex gap-1 overflow-x-auto border-b border-surface-200">
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            className={({ isActive }) => clsx(
              '-mb-px inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
              isActive ? 'border-brand-600 text-brand-700' : 'border-transparent text-surface-500 hover:border-surface-300 hover:text-dark-800',
            )}
          >
            {t.label}
            {t.badge === 'pendingApproval' && pending > 0 && (
              <span className="rounded-full bg-warning-100 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-warning-800">{pending}</span>
            )}
          </NavLink>
        ))}
      </nav>
      {children}
      {compose && (
        <ComposePanel
          key={compose.key}
          initialClientId={compose.clientId || ''}
          draftId={compose.draftId || ''}
          onClose={() => setCompose(null)}
          onSubmitted={() => { announceChange(); }}
        />
      )}
    </PageShell>
  );
}

/** Open the composer from anywhere on a Client mail page. */
export const openCompose = (detail = {}) => window.dispatchEvent(new CustomEvent('client-mail:compose', { detail }));
